import React, { createContext, useContext, useEffect, useRef } from 'react';
import { useUser, useStackApp } from '@stackframe/react';
import { setAuthToken, clearAuthToken } from '../lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const stackUser = useUser();
  const stackApp = useStackApp();
  const tokenSetRef = useRef(false);

  // Transform Stack Auth user to our app's user format
  const user = stackUser ? {
    id: stackUser.id,
    email: stackUser.primaryEmail,
    name: stackUser.displayName || stackUser.primaryEmail?.split('@')[0] || 'User',
    // Stack Auth user properties
    stackUser: stackUser,
  } : null;

  const loading = stackUser === undefined;

  // Update the API token whenever the Stack Auth user changes
  useEffect(() => {
    const updateToken = async () => {
      if (stackUser) {
        try {
          const authJson = await stackUser.getAuthJson();
          if (authJson?.accessToken) {
            setAuthToken(authJson.accessToken);
            tokenSetRef.current = true;
          }
        } catch (error) {
          console.error('Error getting auth token:', error);
          clearAuthToken();
          tokenSetRef.current = false;
        }
      } else {
        clearAuthToken();
        tokenSetRef.current = false;
      }
    };
    
    updateToken();
  }, [stackUser]);

  const login = async (email, password) => {
    // Stack Auth handles login through its own UI
    // This is kept for compatibility but redirects to Stack Auth
    window.location.href = '/handler/sign-in';
  };

  const register = async (email, password, name) => {
    // Stack Auth handles registration through its own UI
    window.location.href = '/handler/sign-up';
  };

  const logout = async () => {
    clearAuthToken();
    tokenSetRef.current = false;
    if (stackUser) {
      await stackUser.signOut();
    }
  };

  // Get the access token for API calls
  const getAccessToken = async () => {
    if (stackUser) {
      const authJson = await stackUser.getAuthJson();
      return authJson?.accessToken || null;
    }
    return null;
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      register, 
      logout,
      getAccessToken,
      stackUser 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
